import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useSiteConfig } from '../contexts/SiteConfigContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { I18nField, ImagePicker } from './AdminHero';
import { Plus, Save, Trash2, Eye, EyeOff, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';

const LUCIDE_OPTIONS = ['Sparkles', 'Scissors', 'Hand', 'Eye', 'Heart', 'Star', 'Palette', 'Brush', 'Flower2', 'Smile', 'Award', 'Gem'];

const AdminServices = () => {
  const { refresh } = useSiteConfig();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const r = await api.get('/admin/services');
    setItems(r.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const update = (idx, patch) => setItems((arr) => arr.map((x, i) => (i === idx ? { ...x, ...patch } : x)));

  const addNew = async () => {
    const obj = {
      name: { de: 'Neuer Service', en: 'New Service', ar: 'خدمة جديدة' },
      description: { de: '', en: '', ar: '' },
      icon: 'Sparkles', image: null, price: '', duration: '',
      order: items.length, visible: true,
    };
    const r = await api.post('/admin/services', obj);
    setItems((arr) => [...arr, r.data]);
    refresh();
  };

  const save = async (s) => {
    try {
      await api.put(`/admin/services/${s.id}`, s);
      toast.success('Saved');
      refresh();
    } catch { toast.error('Save failed'); }
  };

  const remove = async (s) => {
    if (!window.confirm('Delete this service?')) return;
    await api.delete(`/admin/services/${s.id}`);
    setItems((arr) => arr.filter((x) => x.id !== s.id));
    refresh();
  };

  const move = async (idx, dir) => {
    const ni = idx + dir;
    if (ni < 0 || ni >= items.length) return;
    const a = { ...items[idx], order: ni };
    const b = { ...items[ni], order: idx };
    setItems((arr) => arr.map((x, i) => i === idx ? a : i === ni ? b : x).sort((x, y) => x.order - y.order));
    await Promise.all([api.put(`/admin/services/${a.id}`, a), api.put(`/admin/services/${b.id}`, b)]);
    refresh();
  };

  if (loading) return <div className="text-gray-500">Loading...</div>;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-[#5a1d3b]" style={{ fontWeight: 500 }}>Services</h1>
          <p className="text-gray-500 text-sm">Manage all the services shown on the website.</p>
        </div>
        <Button onClick={addNew} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full h-11 px-5 gap-2">
          <Plus className="w-4 h-4" /> Add Service
        </Button>
      </div>

      <div className="space-y-4">
        {items.map((s, idx) => (
          <details key={s.id} className="bg-white border border-gray-200 rounded-xl group" open={idx === 0}>
            <summary className="px-5 py-4 cursor-pointer flex items-center gap-3 list-none">
              <span className="flex-1 font-medium text-[#5a1d3b]">
                {s.name?.de || s.name?.en || 'Untitled'}
              </span>
              {!s.visible && <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">Hidden</span>}
              <button onClick={(e) => { e.preventDefault(); move(idx, -1); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" disabled={idx === 0}>
                <ArrowUp className="w-4 h-4" />
              </button>
              <button onClick={(e) => { e.preventDefault(); move(idx, 1); }} className="p-1.5 hover:bg-gray-100 rounded text-gray-500" disabled={idx === items.length - 1}>
                <ArrowDown className="w-4 h-4" />
              </button>
            </summary>
            <div className="px-5 pb-5 pt-2 border-t border-gray-100 space-y-5">
              <I18nField label="Name" value={s.name} onChange={(v) => update(idx, { name: v })} />
              <I18nField label="Description" value={s.description} onChange={(v) => update(idx, { description: v })} multiline />
              <div className="grid sm:grid-cols-3 gap-4">
                <div>
                  <Label className="mb-2 block text-sm">Icon</Label>
                  <select
                    value={s.icon || 'Sparkles'}
                    onChange={(e) => update(idx, { icon: e.target.value })}
                    className="w-full h-10 border border-gray-200 rounded-lg px-3 text-sm"
                  >
                    {LUCIDE_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="mb-2 block text-sm">Price (optional)</Label>
                  <Input value={s.price || ''} onChange={(e) => update(idx, { price: e.target.value })} placeholder="e.g. 45 €" className="h-10" />
                </div>
                <div>
                  <Label className="mb-2 block text-sm">Duration (optional)</Label>
                  <Input value={s.duration || ''} onChange={(e) => update(idx, { duration: e.target.value })} placeholder="e.g. 60 min" className="h-10" />
                </div>
              </div>
              <ImagePicker label="Service Image (optional, replaces icon)" value={s.image} onChange={(v) => update(idx, { image: v })} mode="cover" />
              <div className="flex items-center justify-between border-t border-gray-100 pt-4">
                <button
                  onClick={() => update(idx, { visible: !s.visible })}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#c4477a]"
                >
                  {s.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  {s.visible ? 'Visible' : 'Hidden'}
                </button>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={() => remove(s)} className="text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-1.5" /> Delete
                  </Button>
                  <Button onClick={() => save(s)} className="bg-[#c4477a] hover:bg-[#a83668] text-white rounded-full px-5">
                    <Save className="w-4 h-4 mr-1.5" /> Save
                  </Button>
                </div>
              </div>
            </div>
          </details>
        ))}
        {items.length === 0 && <p className="text-center text-gray-500 py-12">No services yet. Click &quot;Add Service&quot; above.</p>}
      </div>
    </div>
  );
};

export default AdminServices;
